import moment from 'moment'
import { rem } from 'polished'
import type { MouseEvent } from 'react'
import { memo } from 'react'
import styled from 'styled-components'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getItineraryUniqueKey } from 'lib/cruises/cruiseUtils'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import type { CruiseDeparture } from './ChatCruiseAddToTripActionUtils'

type CruiseDateValue = string | null | undefined

const DepartureModalContent = styled(Group)`
  > * + * {
    margin-top: ${rem(20)};
  }
`

const Section = styled(Group)`
  > * + * {
    margin-top: ${rem(8)};
  }
`

const DepartureList = styled(Group)`
  > * + * {
    margin-top: ${rem(8)};
  }
`

const DepartureCard = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  overflow: hidden;
  width: 100%;

  &.selected {
    border-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    box-shadow: inset 0 0 0 ${rem(1)} ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

const DepartureSelectButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(12)};
  padding: ${rem(16)};
  text-align: start;
  width: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-columns: minmax(0, 1fr) auto auto;
    gap: ${rem(24)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const DateRange = styled(Group)`
  min-width: 0;
`

const DateColumns = styled(Group)`
  column-gap: ${rem(16)};
`

const DateColumn = styled(Group)`
  min-width: 0;

  ${mediaQueryUp.tablet} {
    min-width: ${rem(100)};
  }
`

const PriceColumn = styled(Group)`
  grid-column: 1 / -1;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    grid-column: auto;
    min-width: ${rem(112)};
  }
`

const ItineraryToggleButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;
  display: flex;
  font: inherit;
  gap: ${rem(4)};
  padding: ${rem(10)} ${rem(16)} ${rem(12)};
  text-align: start;
  text-decoration: underline;
  width: 100%;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const RotatingChevron = styled.span`
  align-items: center;
  display: flex;
  transform: rotate(0deg);
  transition: transform 200ms;

  &.open {
    transform: rotate(180deg);
  }
`

const ItineraryPanel = styled(Group)`
  border-top: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  max-height: ${rem(240)};
  overflow-y: auto;
  padding: ${rem(12)} ${rem(16)} ${rem(16)};
`

const ItineraryItem = styled(Group)`
  border-inline-start: ${rem(2)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding-inline-start: ${rem(12)};

  > * + * {
    margin-top: ${rem(2)};
  }
`

const SelectionIndicator = styled.span`
  align-items: center;
  border: ${rem(2)} solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: 50%;
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  display: flex;
  height: ${rem(24)};
  justify-content: center;
  width: ${rem(24)};

  &.selected {
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
    border-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  }
`

interface CruiseDepartureModalLabels {
  atSeaLabel: string
  availableDeparturesHeading: string
  fromLabel: string
  noDeparturesMessage: string
  priceUnavailableLabel: string
  seeItineraryLabel: string
  tripDatesHeading: string
}

interface Props {
  departures: Array<CruiseDeparture>
  expandedDepartureId?: string
  formatItineraryDayLabel: (day: number) => string
  isStreaming: boolean
  itineraryItems: Array<App.CruiseItineraryItem>
  labels: CruiseDepartureModalLabels
  onDepartureClick: (event: MouseEvent<HTMLElement>) => void
  onItineraryToggle: (event: MouseEvent<HTMLElement>) => void
  selectedDepartureId?: string
  tripDatesSummary?: string
}

function formatDepartureDate(date: CruiseDateValue): string {
  if (!date) return ''
  const intl = getCurrentIntl()
  return intlDateFormatter(date, INTL_DMY_CASUAL_FORMAT, intl.locale)
}

function formatWeekday(date: CruiseDateValue): string {
  if (!date) return ''
  return moment(date).format('dddd')
}

function getDeparturePrice(departure: CruiseDeparture): number | undefined {
  if ('lowestPriceTotal' in departure && departure.lowestPriceTotal != null) return departure.lowestPriceTotal
  if ('lowestOverallPriceDetails' in departure) return departure.lowestOverallPriceDetails?.total
  return undefined
}

function getItineraryItemTitle(item: App.CruiseItineraryItem, atSeaLabel: string): string {
  return item.title || item.port?.name || atSeaLabel
}

function ChatCruiseDepartureModalContent({
  departures,
  expandedDepartureId,
  formatItineraryDayLabel,
  isStreaming,
  itineraryItems,
  labels,
  onDepartureClick,
  onItineraryToggle,
  selectedDepartureId,
  tripDatesSummary,
}: Props) {
  return (
    <DepartureModalContent direction="vertical">
      {tripDatesSummary && (
        <Section direction="vertical">
          <BodyTextBlock variant="large" weight="bold">
            {labels.tripDatesHeading}
          </BodyTextBlock>
          <BodyTextBlock variant="medium">
            {tripDatesSummary}
          </BodyTextBlock>
        </Section>
      )}
      <Section direction="vertical">
        <BodyTextBlock variant="large" weight="bold">
          {labels.availableDeparturesHeading}
        </BodyTextBlock>
        {departures.length === 0 && (
          <MessageBanner kind="warning">
            {labels.noDeparturesMessage}
          </MessageBanner>
        )}
        {departures.length > 0 && (
          <DepartureList direction="vertical">
            {departures.map((departure) => {
              const departurePrice = getDeparturePrice(departure)
              const isSelected = departure.id === selectedDepartureId
              const isItineraryExpanded = departure.id === expandedDepartureId
              const canShowItinerary = itineraryItems.length > 0

              return (
                <DepartureCard
                  key={departure.id}
                  className={isSelected ? 'selected' : undefined}
                >
                  <DepartureSelectButton
                    aria-pressed={isSelected}
                    data-departure-id={departure.id}
                    type="button"
                    disabled={isStreaming}
                    onClick={onDepartureClick}
                  >
                    <DateRange direction="vertical" gap={8}>
                      <DateColumns direction="horizontal" verticalAlign="center">
                        <DateColumn direction="vertical" gap={2}>
                          <HeadingTextBlock variant="heading6">
                            {formatDepartureDate(departure.departureDate)}
                          </HeadingTextBlock>
                          <BodyTextBlock variant="small">
                            {formatWeekday(departure.departureDate)}
                          </BodyTextBlock>
                        </DateColumn>
                        <LineArrowRightIcon size="S" />
                        <DateColumn direction="vertical" gap={2}>
                          <HeadingTextBlock variant="heading6">
                            {formatDepartureDate(departure.arrivalDate)}
                          </HeadingTextBlock>
                          <BodyTextBlock variant="small">
                            {formatWeekday(departure.arrivalDate)}
                          </BodyTextBlock>
                        </DateColumn>
                      </DateColumns>
                    </DateRange>
                    <PriceColumn direction="vertical" gap={2}>
                      {departurePrice !== undefined && (
                        <>
                          <BodyTextBlock variant="small">
                            {labels.fromLabel}
                          </BodyTextBlock>
                          <PriceRowPrice
                            price={departurePrice}
                            saleUnit=""
                            size="M"
                            wrap="word-wrap"
                          />
                        </>
                      )}
                      {departurePrice === undefined && (
                        <BodyTextBlock variant="small">
                          {labels.priceUnavailableLabel}
                        </BodyTextBlock>
                      )}
                    </PriceColumn>
                    <SelectionIndicator className={isSelected ? 'selected' : undefined}>
                      {isSelected && <LineCheckIcon size="S" />}
                    </SelectionIndicator>
                  </DepartureSelectButton>
                  {canShowItinerary && (
                    <ItineraryToggleButton
                      aria-expanded={isItineraryExpanded}
                      data-departure-id={departure.id}
                      disabled={isStreaming}
                      type="button"
                      onClick={onItineraryToggle}
                    >
                      <BodyTextBlock variant="small">
                        {labels.seeItineraryLabel}
                      </BodyTextBlock>
                      <RotatingChevron className={isItineraryExpanded ? 'open' : undefined}>
                        <LineAngleDownIcon size="S" />
                      </RotatingChevron>
                    </ItineraryToggleButton>
                  )}
                  {isItineraryExpanded && (
                    <ItineraryPanel direction="vertical" gap={12}>
                      {itineraryItems.map(item => (
                        <ItineraryItem
                          key={getItineraryUniqueKey(item)}
                          direction="vertical"
                        >
                          <BodyTextBlock variant="small">
                            {formatItineraryDayLabel(item.startDay)}
                          </BodyTextBlock>
                          <BodyTextBlock variant="medium" weight="bold">
                            {getItineraryItemTitle(item, labels.atSeaLabel)}
                          </BodyTextBlock>
                          {!!item.description && (
                            <BodyTextBlock variant="small">
                              {item.description}
                            </BodyTextBlock>
                          )}
                        </ItineraryItem>
                      ))}
                    </ItineraryPanel>
                  )}
                </DepartureCard>
              )
            })}
          </DepartureList>
        )}
      </Section>
    </DepartureModalContent>
  )
}

export default memo(ChatCruiseDepartureModalContent)
